import { MessageSquareText } from 'lucide-react';

import { GapDraftReviewStatus, type GapDraftView } from '@momentum/types';

import { titleOf } from '../lib/derive-title';
import { titleCase } from '../lib/format';
import { KEEP_STATUS } from '../lib/queue';
import { InlineMarkdown } from './InlineMarkdown';

/**
 * The verdict as the row's `data-verdict` attribute, which is what the
 * stylesheet colours the dot and the flag off. Empty for an undecided draft, so
 * "no verdict yet" needs no third colour.
 */
function verdictAttribute(status: GapDraftReviewStatus): '' | 'keep' | 'drop' {
  if (status === GapDraftReviewStatus.PENDING) return '';
  return status === KEEP_STATUS ? 'keep' : 'drop';
}

interface QueueRowProps {
  draft: GapDraftView;
  isCurrent: boolean;
  onSelect: (draftId: string) => void;
}

/**
 * One draft in the queue.
 *
 * Four lines, in the order a reviewer scans them: what it claims, where it came
 * from in the pipeline, which run produced it, and whether it has been decided.
 * The pipeline line stays raw — `C3 · Requirement coverage`, not a friendly
 * paraphrase — because the reviewer is a developer checking detector behaviour.
 */
export function QueueRow({ draft, isCurrent, onSelect }: QueueRowProps) {
  const verdict = verdictAttribute(draft.review.status);
  const commentCount = draft.review.comments.length;

  return (
    <button
      className="gr-row"
      type="button"
      aria-current={isCurrent}
      data-verdict={verdict}
      data-reviewed={Boolean(verdict)}
      onClick={() => onSelect(draft._id)}
    >
      <span className="gr-row-top">
        <span className="gr-row-dot" data-verdict={verdict} />
        <span className="gr-row-title">
          <InlineMarkdown text={titleOf(draft)} />
        </span>
      </span>

      <span className="gr-row-meta">
        <span className="gr-row-code">
          {draft.source.metadata.detector} · {titleCase(draft.source.claimType)}
        </span>
        <span className="gr-chip">{draft.source.confidence}</span>
        {draft.source.isHypothesis && (
          <span className="gr-chip" data-tone="amber">
            hyp
          </span>
        )}
      </span>

      {/* Which run produced this gap. The same id on every row of the queue, and
          kept anyway: it is what a reviewer pastes into a script or a thread, and
          reaching for it in the picker above — or worse, retyping it from a
          glance — is the friction this line exists to remove. */}
      <span className="gr-row-run" title={draft.executionId}>
        {draft.executionId}
      </span>

      <span className="gr-row-status">
        <span className="gr-row-flag" data-verdict={verdict}>
          {verdict ? (
            <>
              Reviewed · <b>{verdict === 'keep' ? 'Kept' : 'Dropped'}</b>
              {/* How sure the reviewer was, on the row where they will look for
                  it — scanning a filter for the shaky calls is the whole reason
                  the level is recorded. Absent on a draft decided before the
                  field existed; the row is too narrow to spell that out, and the
                  card in Final review does. */}
              {draft.review.reviewConfidence !== undefined &&
                ` · ${draft.review.reviewConfidence}`}
            </>
          ) : (
            'Not reviewed'
          )}
        </span>
        {commentCount > 0 && (
          <>
            <i className="gr-row-sep" />
            <span className="gr-row-comments">
              <MessageSquareText />
              {commentCount}
            </span>
          </>
        )}
      </span>
    </button>
  );
}
